/**
 * YourBeans Academy Configuration Module
 * Handles multi-mode detection and configuration
 *
 * Based on Numero Uno Cafe Platform architecture (proven, scalable)
 */

export type AppMode = 'academy' | 'vendor' | 'shopify' | 'guest';

export interface UserState {
  mode: AppMode;
  isAuthenticated: boolean;
  userId?: string;
  email?: string;
  name?: string;
  vendorId?: string;        // MyShop.coffee vendor ID
  storeName?: string;       // MyShop.coffee store name
  avatarUrl?: string;       // User avatar URL
  shopifyStoreId?: string;  // Shopify store domain
  academyProgress?: AcademyProgress;
}

export interface AcademyProgress {
  enrolledAt: string;
  modulesCompleted: string[];
  currentModule?: string;
  totalPoints: number;
  certificateEarned: boolean;
}

export interface AcademyConfig {
  appName: string;
  tagline: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  socialLinks: {
    instagram: string;
    facebook: string;
    youtube?: string;
  };
  features: {
    masterclass: boolean;
    mentoring: boolean;
    resources: boolean;
    community: boolean;
    vendorDashboard: boolean;
  };
}

/**
 * Default YourBeans branding configuration
 */
export const defaultConfig: AcademyConfig = {
  appName: 'YourBeans Coffee Academy',
  tagline: 'Learn. Connect. Launch Your Coffee Business.',
  logo: 'https://yourbeans.com.au/wp-content/uploads/2023/01/cropped-cropped-cropped-cropped-YB-LOGO-NEW-1-1-e1764911260195.png',
  primaryColor: '#806a5b',      // Warm coffee brown (from yourbeans.com.au)
  secondaryColor: '#1B233B',    // Deep navy (from MyShop ecosystem)
  accentColor: '#589A89',       // Teal green (success/growth)
  socialLinks: {
    instagram: 'https://instagram.com/yourbeanscoffee',
    facebook: 'https://facebook.com/groups/coffeeentrepreneursaustralia',
    youtube: 'https://youtube.com/@yourbeanscoffee'
  },
  features: {
    masterclass: true,
    mentoring: true,
    resources: true,
    community: true,
    vendorDashboard: true
  }
};

/**
 * Detect app mode from URL and authentication state
 *
 * URL Patterns:
 * - yourbeans.com.au/app                    → guest/academy
 * - yourbeans.com.au/app?mode=vendor        → vendor dashboard
 * - yourbeans.com.au/app?mode=shopify       → shopify user
 * - academy.yourbeans.com.au                → academy (subdomain)
 * - app.yourbeans.com.au                    → academy (alt subdomain)
 */
export const detectAppMode = (): AppMode => {
  const urlParams = new URLSearchParams(window.location.search);

  // Check URL parameter first (explicit mode)
  const modeParam = urlParams.get('mode');
  if (modeParam === 'vendor') return 'vendor';
  if (modeParam === 'shopify') return 'shopify';

  // Check for vendor token in URL (OAuth callback)
  if (urlParams.get('vendor_token')) return 'vendor';
  if (urlParams.get('shopify_token')) return 'shopify';

  // Check localStorage for existing session
  const savedMode = localStorage.getItem('yourbeans_app_mode');
  if (savedMode === 'vendor' || savedMode === 'shopify') {
    return savedMode as AppMode;
  }

  // Default to academy mode
  return 'academy';
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('yourbeans_token');
  const user = localStorage.getItem('yourbeans_user');
  return !!(token && user);
};

/**
 * Get current user state
 */
export const getUserState = (): UserState => {
  const mode = detectAppMode();
  const authenticated = isAuthenticated();

  if (!authenticated) {
    return { mode: 'guest', isAuthenticated: false };
  }

  try {
    const userData = JSON.parse(localStorage.getItem('yourbeans_user') || '{}');
    return {
      mode,
      isAuthenticated: true,
      userId: userData.id,
      email: userData.email,
      name: userData.name,
      vendorId: userData.vendorId,
      storeName: userData.storeName,
      avatarUrl: userData.avatarUrl,
      shopifyStoreId: userData.shopifyStoreId,
      academyProgress: userData.academyProgress
    };
  } catch {
    return { mode: 'guest', isAuthenticated: false };
  }
};

/**
 * Save user session
 */
export const saveUserSession = (token: string, userData: Partial<UserState>): void => {
  localStorage.setItem('yourbeans_token', token);
  localStorage.setItem('yourbeans_user', JSON.stringify(userData));
  if (userData.mode) {
    localStorage.setItem('yourbeans_app_mode', userData.mode);
  }
};

/**
 * Clear user session (logout)
 */
export const clearUserSession = (): void => {
  localStorage.removeItem('yourbeans_token');
  localStorage.removeItem('yourbeans_user');
  localStorage.removeItem('yourbeans_app_mode');
};

/**
 * Apply theme colors to CSS variables
 */
export const applyTheme = (config: AcademyConfig = defaultConfig): void => {
  const root = document.documentElement;
  root.style.setProperty('--yb-primary', config.primaryColor);
  root.style.setProperty('--yb-secondary', config.secondaryColor);
  root.style.setProperty('--yb-accent', config.accentColor);
};

/**
 * Get MyShop.coffee OAuth URL
 */
export const getMyShopOAuthUrl = (): string => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/app?mode=vendor`);
  return `https://myshop.coffee/oauth/authorize?client_id=yourbeans_academy&redirect_uri=${redirectUri}&response_type=code`;
};

/**
 * Get Shopify OAuth URL (for YourBeans Shopify app users)
 */
export const getShopifyOAuthUrl = (shopDomain: string): string => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/app?mode=shopify`);
  return `https://${shopDomain}/admin/oauth/authorize?client_id=yourbeans_shopify&redirect_uri=${redirectUri}`;
};
